import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, MapPin, Clock, ArrowRight, Calendar } from "lucide-react";
import api from "../../lib/api";
import { useSiteConfig } from "../../context/SiteConfigContext";
import HeroImage from "../../components/HeroImage";
import SeoHead from "../../components/SeoHead";

export default function Careers() {
    const { config } = useSiteConfig();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/jobs").then(({ data }) => setJobs(data)).finally(() => setLoading(false));
    }, []);

    if (!config) return null;

    const featured = jobs.filter((j) => j.featured);
    const standard = jobs.filter((j) => !j.featured);

    return (
        <div data-testid="careers-page">
            <SeoHead
                pageKey="careers"
                title={`Careers — ${config.site_name}`}
                description="Open positions, internships, and volunteer opportunities at YACRRD across South Sudan."
                image={config.careers_hero_image}
            />

            {/* HERO */}
            <section className="relative min-h-[55vh] flex items-end overflow-hidden" data-testid="careers-hero">
                <HeroImage
                    src={config.careers_hero_image}
                    alt="Join YACRRD"
                    animation={config.hero_animation || "ken-burns"}
                    overlay={config.hero_overlay_style || "dark"}
                    overlayOpacity={config.hero_overlay_opacity ?? 55}
                />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 w-full text-white">
                    <div className="text-xs uppercase tracking-[0.22em] mb-4 opacity-90">Careers / Jobs</div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl mb-6">
                        {config.careers_hero_title}
                    </h1>
                    <p className="text-base sm:text-lg text-white/90 max-w-2xl">
                        {config.careers_hero_subtitle}
                    </p>
                </div>
            </section>

            {/* JOBS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="careers-jobs">
                {loading ? (
                    <div className="text-muted-foreground text-sm">Loading positions…</div>
                ) : jobs.length === 0 ? (
                    <div className="bg-card border border-border rounded-sm p-12 text-center" data-testid="careers-empty">
                        <Briefcase size={32} className="text-muted-foreground mx-auto mb-4" />
                        <h2 className="font-display text-3xl mb-3">No open roles right now</h2>
                        <p className="text-muted-foreground max-w-xl mx-auto mb-8">{config.careers_empty_state}</p>
                        <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-primary text-primary-foreground text-sm font-medium">
                            Send an open application <ArrowRight size={14} />
                        </Link>
                    </div>
                ) : (
                    <>
                        {featured.length > 0 && (
                            <div className="mb-16">
                                <div className="text-xs uppercase tracking-[0.22em] text-secondary mb-6">Featured Positions</div>
                                <div className="grid lg:grid-cols-2 gap-5">
                                    {featured.map((j) => <JobCard key={j.id} j={j} featured />)}
                                </div>
                            </div>
                        )}
                        {standard.length > 0 && (
                            <div>
                                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-6">All Open Positions ({standard.length})</div>
                                <div className="space-y-3">
                                    {standard.map((j) => <JobRow key={j.id} j={j} />)}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* CTA */}
            <section className="bg-muted/40 py-20" data-testid="careers-cta">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
                    <h2 className="font-display text-4xl mb-4">Don't see the right role?</h2>
                    <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                        We're always interested in passionate, mission-aligned talent. Send your CV and a short note about what you'd love to do.
                    </p>
                    <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium">
                        Send an open application <ArrowRight size={14} />
                    </Link>
                </div>
            </section>
        </div>
    );
}

const JobCard = ({ j, featured }) => (
    <Link
        to={`/careers/${j.slug}`}
        className={`block rounded-sm p-7 border bg-card hover:border-primary/60 transition-colors group ${featured ? "border-secondary/40" : "border-border"}`}
        data-testid={`job-card-${j.slug}`}
    >
        <div className="text-xs uppercase tracking-[0.22em] text-secondary mb-2">{j.employment_type}{j.department && ` · ${j.department}`}</div>
        <h3 className="font-display text-2xl leading-tight mb-3 group-hover:text-primary transition-colors">{j.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-3 mb-5">{j.summary}</p>
        <div className="flex flex-wrap gap-4 text-xs text-foreground/70">
            <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {j.location}</span>
            {j.deadline && <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> Apply by {j.deadline}</span>}
        </div>
    </Link>
);

const JobRow = ({ j }) => (
    <Link
        to={`/careers/${j.slug}`}
        className="flex items-center justify-between gap-4 p-5 rounded-sm border border-border bg-card hover:border-primary/60 transition-colors group"
        data-testid={`job-row-${j.slug}`}
    >
        <div className="flex-1 min-w-0">
            <h3 className="font-display text-xl group-hover:text-primary transition-colors truncate">{j.title}</h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Briefcase size={11} /> {j.employment_type}</span>
                <span className="inline-flex items-center gap-1"><MapPin size={11} /> {j.location}</span>
                {j.deadline && <span className="inline-flex items-center gap-1"><Clock size={11} /> Apply by {j.deadline}</span>}
            </div>
        </div>
        <ArrowRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
    </Link>
);
